import { MRT_RowData } from "material-react-table";
import { Stack, useTheme, RadioGroup, FormControlLabel, Radio, FormControl, FormLabel, Divider } from "@mui/material";
import { CustomMRTFilter } from "../mrt.types";
import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { SelectManyFromFilter } from "../SelectManyFromFilter/SelectManyFromFilter";
import type { EmptyNullValue } from "../EmptyNullFilter";
import type { ComponentAppearance } from "src/components/appearance";

export type TagsFilterValue = {
    values?: string | string[];
    emptyNull?: EmptyNullValue;
};

type TagsFilterProps<T extends MRT_RowData> = {
    appearance?: ComponentAppearance;
    selectOptions: {
        kind: "checkbox";
        values: Array<{ id: string | number; name: string }>;
        getId: (item: any) => string | number;
        getName: (item: any) => string;
    };
    mrtProps: CustomMRTFilter<T>;
    searchApi?: any;
};

export const TagsFilter = <T extends MRT_RowData>(props: TagsFilterProps<T>) => {
    const { appearance = "legacy", selectOptions, mrtProps, searchApi } = props;
    const theme = useTheme();
    const { t } = useTranslation();

    const initFilterValue = mrtProps.column.getFilterValue() as
        | TagsFilterValue
        | string
        | string[]
        | EmptyNullValue
        | undefined;

    // Parse initial values
    let initialEmptyNull: string = "all";
    let initialValues: string | string[] | undefined = undefined;

    if (typeof initFilterValue === "string" && (initFilterValue === "empty" || initFilterValue === "notEmpty")) {
        // Old EmptyNullValue format
        initialEmptyNull = initFilterValue;
    } else if (
        Array.isArray(initFilterValue) ||
        (typeof initFilterValue === "string" && initFilterValue !== "empty" && initFilterValue !== "notEmpty")
    ) {
        // Array of values or single string value
        initialValues = initFilterValue;
    } else if (initFilterValue && typeof initFilterValue === "object") {
        // TagsFilterValue format
        initialEmptyNull = initFilterValue.emptyNull || "all";
        initialValues = initFilterValue.values;
    }

    const [emptyNullValue, setEmptyNullValue] = useState<string>(initialEmptyNull);
    const [selectedValues, setSelectedValues] = useState<string | string[] | null | undefined>(initialValues);

    // Update filter value when any value changes
    useEffect(() => {
        if (!mrtProps.column) return;

        const timeoutId = setTimeout(() => {
            const hasEmptyNull = emptyNullValue !== "all";
            const hasValues =
                selectedValues !== undefined &&
                selectedValues !== null &&
                (Array.isArray(selectedValues) ? selectedValues.length > 0 : selectedValues !== "");

            // If no filters are set, clear the filter completely
            if (!hasEmptyNull && !hasValues) {
                mrtProps.column.setFilterValue(undefined);
                return;
            }

            // Build filter value
            const filterValue: TagsFilterValue = {};

            if (hasValues) filterValue.values = selectedValues;
            if (hasEmptyNull) filterValue.emptyNull = emptyNullValue as EmptyNullValue;

            mrtProps.column.setFilterValue(filterValue);
        }, 100);

        return () => clearTimeout(timeoutId);
    }, [selectedValues, emptyNullValue, mrtProps.column]);

    // Create a modified mrtProps that updates our local state
    const modifiedMrtProps = {
        ...mrtProps,
        column: {
            ...mrtProps.column,
            getFilterValue: () => selectedValues,
            setFilterValue: (value: any) => setSelectedValues(value),
        },
    };

    return (
        <Stack gap={theme.spacing(2)} sx={{ padding: theme.spacing(1), minWidth: "250px" }}>
            {/* Empty/Not Empty Filter */}
            <FormControl component="fieldset" size="small">
                <FormLabel component="legend" sx={{ fontSize: "0.875rem", marginBottom: theme.spacing(0.5) }}>
                    {t("grid.filter.tag.emptyLabel", "État")}
                </FormLabel>
                <RadioGroup
                    value={emptyNullValue}
                    onChange={(e) => setEmptyNullValue(e.target.value)}
                    aria-label={t("grid.filter.emptyNull.ariaLabel", "Filter by empty or not empty")}
                >
                    <FormControlLabel
                        value="all"
                        control={<Radio size="small" />}
                        label={t("grid.filter.emptyNull.all", "Tous")}
                    />
                    <FormControlLabel
                        value="empty"
                        control={<Radio size="small" />}
                        label={t("grid.filter.emptyNull.empty", "Vide")}
                    />
                    <FormControlLabel
                        value="notEmpty"
                        control={<Radio size="small" />}
                        label={t("grid.filter.emptyNull.notEmpty", "Non vide")}
                    />
                </RadioGroup>
            </FormControl>

            <Divider />

            {/* Values Filter */}
            <Stack gap={theme.spacing(1)}>
                <FormLabel component="legend" sx={{ fontSize: "0.875rem" }}>
                    {t("grid.filter.tag.valuesLabel", "Valeurs")}
                </FormLabel>
                <SelectManyFromFilter
                    appearance={appearance}
                    selectOptions={selectOptions}
                    mrtProps={modifiedMrtProps}
                    searchApi={searchApi}
                />
            </Stack>
        </Stack>
    );
};
